import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import {
  CheckCircleRounded,
  WorkspacePremiumRounded,
  PeopleAltRounded,
  ReceiptLongRounded,
  AutorenewRounded,
  CreditCardRounded,
} from "@mui/icons-material";
import SubscriptionService from "../../../services/SubscriptionService";
import AppService from "../../../services/AppService";
import { showNotification } from "../../../utils/notifications";

const TEAL = "#0E6B6B";
const TEAL_TINT = "rgba(14, 107, 107, 0.06)";

const STATUS_COLORS = {
  ACTIVE: "success",
  FREE: "default",
  PENDING_PAYMENT: "warning",
  EXPIRED: "error",
  CANCELLED: "error",
};

// Display ordering of tiers, used to label CTAs as Upgrade vs Switch.
const TIER_RANK = { FREE: 0, BASIC: 1, PREMIUM: 2, ENTERPRISE: 3 };
// Which tier is badged "Most popular" (a display choice, not data-driven).
const RECOMMENDED_TIER = "PREMIUM";

const monthsOf = (p) => (p.periodUnit === "YEAR" ? p.periodCount * 12 : p.periodCount);

const cycleLabel = (unit, count) => {
  if (unit === "YEAR") return count === 1 ? "Yearly" : `${count} Years`;
  if (count === 1) return "Monthly";
  if (count === 12) return "Yearly";
  return `${count} Months`;
};

const perLabel = (unit, count) => {
  if (unit === "YEAR") return count === 1 ? "per year" : `per ${count} years`;
  if (count === 1) return "per month";
  if (count === 12) return "per year";
  return `for ${count} months`;
};

const featureLabel = (key) => (key === "EMPLOYEE_COUNT" ? "Employees" : key);

const formatMoney = (amount, currency = "AED") => {
  if (amount == null) return "";
  try {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${amount} ${currency}`;
  }
};

const round2 = (n) => Math.round(n * 100) / 100;

// Clean full-width status indicator used in place of a washed-out disabled button.
const Pill = ({ tone = "muted", icon, children }) => (
  <Box
    sx={{
      width: "100%",
      height: 40,
      borderRadius: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      fontSize: 14,
      fontWeight: 600,
      bgcolor: tone === "success" ? "rgba(76,175,80,0.12)" : "grey.100",
      color: tone === "success" ? "success.main" : "text.secondary",
      border: "1px solid",
      borderColor: tone === "success" ? "rgba(76,175,80,0.45)" : "divider",
    }}
  >
    {icon}
    {children}
  </Box>
);

/** Customer: current plan + usage, plan picker with billing-cycle selector and checkout. */
const SubscriptionPage = () => {
  const [current, setCurrent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState(null); // "MONTH-1" etc., global across cards
  const [purchasing, setPurchasing] = useState(false);
  const [gatewayConfig, setGatewayConfig] = useState({ feePercentage: 0, feeFixed: 0 });
  const [confirm, setConfirm] = useState(null); // { plan, price } when the confirm dialog is open
  const [autoRenewOptIn, setAutoRenewOptIn] = useState(true); // purchase dialog opt-in (default ON)
  const [savingAutoRenew, setSavingAutoRenew] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      SubscriptionService.getCurrent(),
      SubscriptionService.getAvailablePlans(),
      SubscriptionService.getPayments(),
    ])
      .then(([cur, planList, pays]) => {
        setCurrent(cur);
        setPlans(Array.isArray(planList) ? planList : []);
        setPayments(Array.isArray(pays) ? pays : []);
        setLoading(false);
      })
      .catch(() => {
        showNotification("Failed to load subscription", "error");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadAll();
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout === "success") {
      showNotification("Payment received. Your subscription will activate shortly.", "success");
    } else if (checkout === "cancelled") {
      showNotification("Payment was cancelled.", "warning");
    }
  }, [loadAll]);

  // NGenius platform-fee config so the confirm dialog can show the surcharge.
  useEffect(() => {
    let active = true;
    AppService.get("/v1/payment-gateways/enabled")
      .then((list) => {
        if (!active) return;
        const ng = (Array.isArray(list) ? list : []).find((g) => g.gatewayKey === "NGENIUS");
        if (ng) {
          setGatewayConfig({
            feePercentage: Number(ng.feePercentage) || 0,
            feeFixed: Number(ng.feeFixed) || 0,
          });
        }
      })
      .catch(() => { /* no config row → no fee shown */ });
    return () => { active = false; };
  }, []);

  // Distinct billing cycles across all plans, shortest first.
  const cycles = useMemo(() => {
    const map = new Map();
    plans.forEach((pl) =>
      (pl.prices || []).forEach((pr) => {
        const key = `${pr.periodUnit}-${pr.periodCount}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            unit: pr.periodUnit,
            count: pr.periodCount,
            months: monthsOf(pr),
            label: cycleLabel(pr.periodUnit, pr.periodCount),
          });
        }
      })
    );
    return Array.from(map.values()).sort((a, b) => a.months - b.months);
  }, [plans]);

  useEffect(() => {
    if (cycles.length && (!selectedCycle || !cycles.find((c) => c.key === selectedCycle))) {
      setSelectedCycle(cycles[0].key);
    }
  }, [cycles, selectedCycle]);

  const priceFor = (plan) =>
    (plan.prices || []).find((p) => `${p.periodUnit}-${p.periodCount}` === selectedCycle);

  const computeFee = (amount) => {
    const pct = round2((amount * (gatewayConfig.feePercentage || 0)) / 100);
    return round2(pct + (gatewayConfig.feeFixed || 0));
  };

  const openConfirm = (plan) => {
    const price = priceFor(plan);
    if (!price) {
      showNotification("This plan isn't available for the selected billing cycle", "error");
      return;
    }
    setAutoRenewOptIn(true);
    setConfirm({ plan, price });
  };

  const proceedToPayment = () => {
    if (!confirm) return;
    setPurchasing(true);
    const base = `${window.location.origin}${window.location.pathname}`;
    SubscriptionService.purchase({
      planPriceId: confirm.price.id,
      paymentGateway: "NGENIUS",
      autoRenew: autoRenewOptIn,
      paymentRedirectUrl: `${base}?checkout=success`,
      paymentCancelUrl: `${base}?checkout=cancelled`,
    })
      .then((res) => {
        if (res && res.paymentUrl) {
          window.location.href = res.paymentUrl;
        } else {
          if (res && res.activatedImmediately) showNotification("Plan activated", "success");
          setPurchasing(false);
          setConfirm(null);
          loadAll();
        }
      })
      .catch((e) => {
        setPurchasing(false);
        showNotification(e?.response?.data?.message || "Failed to start checkout", "error");
      });
  };

  const empLimitText = (plan) => {
    const ent = (plan.entitlements || []).find((e) => e.featureKey === "EMPLOYEE_COUNT");
    if (!ent) return "Unlimited employees";
    return ent.unlimited ? "Unlimited employees" : `Up to ${ent.limitValue} employees`;
  };

  const toggleAutoRenew = (enable) => {
    setSavingAutoRenew(true);
    const call = enable ? SubscriptionService.enableAutoRenew() : SubscriptionService.cancelAutoRenew();
    call
      .then(() => {
        showNotification(enable ? "Auto-renewal enabled" : "Auto-renewal cancelled", "success");
        setSavingAutoRenew(false);
        loadAll();
      })
      .catch((e) => {
        setSavingAutoRenew(false);
        showNotification(e?.response?.data?.message || "Failed to update auto-renewal", "error");
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentRank = current && current.tier != null ? TIER_RANK[current.tier] ?? 0 : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Subscription</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage your plan, track usage, and upgrade when you need more capacity.
      </Typography>

      {/* ---- Current plan + usage ---- */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ px: 2.5, py: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: TEAL,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <WorkspacePremiumRounded />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current plan</Typography>
                <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                  {current && current.planName ? current.planName : "Free"}
                </Typography>
                {current && current.periodEnd && (
                  <Typography variant="caption" color="text.secondary">
                    Renews / expires on {current.periodEnd}
                  </Typography>
                )}
              </Box>
            </Stack>
            {current && current.status && (
              <Chip
                label={current.status.replace("_", " ")}
                color={STATUS_COLORS[current.status] || "default"}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>

          {(current?.usage || []).length > 0 && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Grid container spacing={2}>
                {(current?.usage || []).map((u) => {
                  const pct = u.unlimited ? 0 : u.limit ? Math.min(100, (u.used / u.limit) * 100) : 0;
                  const barColor = pct >= 100 ? "error" : pct >= 80 ? "warning" : "primary";
                  return (
                    <Grid item xs={12} sm={6} key={u.featureKey}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography variant="body2" fontWeight={600}>{featureLabel(u.featureKey)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {u.used} / {u.unlimited ? "Unlimited" : u.limit}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={u.unlimited ? 100 : pct}
                        color={u.unlimited ? "success" : barColor}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---- Auto-renewal + saved card ---- */}
      {(current?.savedCard || current?.autoRenew || current?.autoRenewFailedOn) && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ px: 2.5, py: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 44, height: 44, borderRadius: 2, bgcolor: TEAL, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <AutorenewRounded />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Auto-renewal</Typography>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {current.autoRenew ? "On" : "Off"}
                  </Typography>
                  {current.autoRenew && current.upcomingRenewalDate && !current.autoRenewFailedOn && (
                    <Typography variant="caption" color="text.secondary">
                      Renews on {current.upcomingRenewalDate}
                      {current.upcomingRenewalAmount != null &&
                        ` · ${formatMoney(current.upcomingRenewalAmount, current.upcomingRenewalCurrency || "AED")}`}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {current.autoRenew ? (
                <Button
                  variant="outlined"
                  disabled={savingAutoRenew || !!current.autoRenewFailedOn}
                  onClick={() => toggleAutoRenew(false)}
                  sx={{
                    fontWeight: 600,
                    color: "#F44335",
                    borderColor: "#F44335",
                    "&:hover": { borderColor: "#c62828", bgcolor: "rgba(244,67,53,0.06)" },
                  }}
                >
                  Cancel auto-renewal
                </Button>
              ) : current.savedCard ? (
                <Button
                  variant="contained"
                  disabled={savingAutoRenew || !!current.autoRenewFailedOn}
                  onClick={() => toggleAutoRenew(true)}
                  sx={{ bgcolor: TEAL, "&:hover": { bgcolor: "#0A5151" } }}
                >
                  Enable auto-renewal
                </Button>
              ) : null}
            </Stack>

            {current.autoRenewFailedOn && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your plan lapsed on {current.autoRenewFailedOn} and automatic renewal didn't go
                through. Please renew your plan manually to keep it active — pick your plan below
                and complete the payment.
              </Alert>
            )}

            {current.savedCard && (
              <>
                <Divider sx={{ my: 1.25 }} />
                <Stack direction="row" spacing={2} alignItems="center">
                  <CreditCardRounded sx={{ color: TEAL }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {current.savedCard.scheme || "Card"} · {current.savedCard.maskedPan}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {current.savedCard.cardholderName}
                      {current.savedCard.expiry ? ` · expires ${current.savedCard.expiry}` : ""}
                    </Typography>
                  </Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Plan picker header + billing-cycle selector ---- */}
      <Box sx={{ textAlign: "center", mb: 3.5 }}>
        <Typography variant="h6" sx={{ mb: cycles.length > 1 ? 1.5 : 0 }}>Choose a plan</Typography>
        {cycles.length > 1 && selectedCycle && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select your billing cycle
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ToggleButtonGroup
                value={selectedCycle}
                exclusive
                onChange={(e, v) => v && setSelectedCycle(v)}
                sx={{
                  bgcolor: "grey.100",
                  borderRadius: 999,
                  p: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    m: 0.25,
                    border: 0,
                    borderRadius: "999px !important",
                    textTransform: "none",
                    px: { xs: 2, sm: 3 },
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: 14,
                    color: "text.secondary",
                    "&:hover": { bgcolor: "rgba(14,107,107,0.08)" },
                    "&.Mui-selected": {
                      bgcolor: TEAL,
                      color: "#fff",
                      boxShadow: "0 2px 8px rgba(14,107,107,0.3)",
                      "&:hover": { bgcolor: "#0A5151" },
                    },
                  },
                }}
              >
                {cycles.map((c) => (
                  <ToggleButton key={c.key} value={c.key}>{c.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
        {plans.map((plan) => {
          const isCurrent = current && current.planId === plan.id;
          const recommended = plan.tier === RECOMMENDED_TIER;
          const price = priceFor(plan);
          const isFreeTier = plan.tier === "FREE";
          const displayPrice = price ? (price.discountedPrice != null ? price.discountedPrice : price.price) : null;
          const hasDiscount = price && price.discountedPrice != null && price.discountedPrice < price.price;
          const cur = price ? price.currency || "AED" : "AED";
          const planRank = TIER_RANK[plan.tier] ?? 0;
          const ctaLabel = planRank > currentRank ? "Upgrade" : "Choose plan";

          return (
            <Grid item xs={12} sm={6} md={3} key={plan.id} sx={{ display: "flex" }}>
              <Card
                variant="outlined"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderWidth: recommended || isCurrent ? 2 : 1,
                  borderColor: isCurrent ? "success.main" : recommended ? TEAL : "divider",
                  boxShadow: recommended ? "0 12px 28px 0 rgba(14,107,107,0.12)" : "none",
                  transition: "all 0.25s",
                  "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                }}
              >
                {recommended && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 14,
                      right: 14,
                    }}
                  >
                    <Chip label="Most popular" size="small" sx={{ bgcolor: TEAL, color: "#fff", fontWeight: 600 }} />
                  </Box>
                )}

                <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{plan.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minHeight: 34, display: "block", mt: 0.5 }}>
                    {plan.description}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ my: 2.5, minHeight: 78 }}>
                    {isFreeTier ? (
                      <>
                        <Typography variant="h4" fontWeight={700} sx={{ color: TEAL }}>Free</Typography>
                        <Typography variant="caption" color="text.secondary">forever</Typography>
                      </>
                    ) : price ? (
                      <>
                        {hasDiscount && (
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: "line-through", color: "text.secondary", lineHeight: 1 }}
                          >
                            {formatMoney(price.price, cur)}
                          </Typography>
                        )}
                        <Typography variant="h4" fontWeight={700} sx={{ color: TEAL, lineHeight: 1.2 }}>
                          {formatMoney(displayPrice, cur)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {perLabel(price.periodUnit, price.periodCount)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                        Not offered for this billing cycle
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Features */}
                  <Stack spacing={1} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PeopleAltRounded sx={{ fontSize: 18, color: TEAL }} />
                      <Typography variant="body2">{empLimitText(plan)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleRounded sx={{ fontSize: 18, color: "success.main" }} />
                      <Typography variant="body2" color="text.secondary">All core platform features</Typography>
                    </Stack>
                  </Stack>

                  {/* CTA */}
                  <Box sx={{ mt: 3 }}>
                    {isCurrent ? (
                      <Pill tone="success" icon={<CheckCircleRounded sx={{ fontSize: 18 }} />}>
                        Current plan
                      </Pill>
                    ) : isFreeTier ? (
                      <Pill>Base plan</Pill>
                    ) : !price ? (
                      <Pill>Unavailable</Pill>
                    ) : (
                      <Button
                        fullWidth
                        variant={recommended ? "contained" : "outlined"}
                        color="primary"
                        disabled={purchasing}
                        onClick={() => openConfirm(plan)}
                        sx={
                          recommended
                            ? { height: 40, bgcolor: TEAL, "&:hover": { bgcolor: "#0A5151" } }
                            : { height: 40, borderColor: TEAL, color: TEAL, "&:hover": { borderColor: "#0A5151", bgcolor: TEAL_TINT } }
                        }
                      >
                        {ctaLabel}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ---- Billing history ---- */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <ReceiptLongRounded sx={{ color: TEAL }} />
            <Typography variant="h6">Billing history</Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No payments yet.
                    </TableCell>
                  </TableRow>
                )}
                {payments.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.planName}</TableCell>
                    <TableCell>{formatMoney(p.amount, p.currency)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(p.status || "").replace("_", " ")}
                        color={
                          p.status === "PAID" || p.status === "FREE_GRANT"
                            ? "success"
                            : p.status === "PENDING_PAYMENT"
                            ? "warning"
                            : p.status === "PAYMENT_FAILED"
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{(p.periodStartApplied || "—")} → {(p.periodEndApplied || "—")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ---- Confirm purchase (shows gateway surcharge) ---- */}
      <Dialog open={Boolean(confirm)} onClose={() => !purchasing && setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (() => {
          const amount = confirm.price.discountedPrice != null ? confirm.price.discountedPrice : confirm.price.price;
          const hasDiscount = confirm.price.discountedPrice != null && confirm.price.discountedPrice < confirm.price.price;
          const fee = computeFee(amount);
          const total = round2(amount + fee);
          const cur = confirm.price.currency || "AED";
          return (
            <>
              <DialogTitle sx={{ pb: 0.5 }}>Confirm purchase</DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: TEAL_TINT,
                    border: "1px solid rgba(14,107,107,0.18)",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>{confirm.plan.name} plan</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cycleLabel(confirm.price.periodUnit, confirm.price.periodCount)} subscription
                  </Typography>
                </Box>

                <Grid container rowSpacing={1.25}>
                  <Grid item xs={7}><Typography variant="body2">Subscription price</Typography></Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">
                      {hasDiscount && (
                        <Box component="s" sx={{ color: "text.secondary", mr: 0.75 }}>
                          {formatMoney(confirm.price.price, cur)}
                        </Box>
                      )}
                      {formatMoney(amount, cur)}
                    </Typography>
                  </Grid>

                  {fee > 0 && (
                    <>
                      <Grid item xs={7}><Typography variant="body2">Platform fee</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: "right" }}>
                        <Typography variant="body2">+ {formatMoney(fee, cur)}</Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                  <Grid item xs={7}><Typography variant="body1" fontWeight={700}>Total to pay</Typography></Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body1" fontWeight={700} sx={{ color: TEAL }}>
                      {formatMoney(total, cur)}
                    </Typography>
                  </Grid>
                </Grid>

                {fee > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    The platform fee is charged by the gateway on top of your subscription.
                  </Typography>
                )}

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: TEAL_TINT,
                    border: "1px solid rgba(14,107,107,0.25)",
                  }}
                >
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", m: 0 }}
                    control={
                      <Switch
                        checked={autoRenewOptIn}
                        onChange={(e) => setAutoRenewOptIn(e.target.checked)}
                        sx={{ mt: -0.5 }}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ fontWeight: 700, color: "text.primary", display: "block", mb: 0.25 }}>
                          Enable auto-renewal
                        </Box>
                        Securely save my card and renew automatically before expiry.
                        The card is stored by the payment gateway, not Thaklis.
                      </Typography>
                    }
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirm(null)} disabled={purchasing} color="inherit">Cancel</Button>
                <Button
                  variant="contained"
                  onClick={proceedToPayment}
                  disabled={purchasing}
                  startIcon={purchasing ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ bgcolor: TEAL, "&:hover": { bgcolor: "#0A5151" } }}
                >
                  {purchasing ? "Redirecting…" : "Proceed to payment"}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default SubscriptionPage;
